/**
 * 파일 업로드 관련 API 서비스
 */

import apiClient from './axiosInstance';

export interface ImageUploadResponse {
  url: string;
  key: string;
  etag: string;
}

interface UploadApiResponse {
  error: number;
  data: {
    location: string;
    key: string;
    etag: string;
  };
}

/**
 * Base64 이미지를 파일로 업로드
 */
export const uploadBase64Image = async (
  base64Data: string,
  filename?: string
): Promise<ImageUploadResponse> => {
  // MIME 타입 추출
  const matches = base64Data.match(/^data:([A-Za-z-+/]+);base64,/);
  const mimeType = matches ? matches[1] : 'image/png';
  const extension = mimeType.split('/')[1];
  
  // 파일명 생성 - 타임스탬프와 랜덤 문자열 추가
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const finalFilename = filename || `${timestamp}_${randomStr}.${extension}`;

  try {
    const response = await apiClient.post<UploadApiResponse>('/api/v1/upload/base64', {
      base64: base64Data,
      filename: finalFilename,
      path: 'ai-reviews' // AI 리뷰 이미지는 특정 경로에 저장
    });

    // API 응답 구조에서 실제 데이터 추출
    return {
      url: response.data.data.location,
      key: response.data.data.key,
      etag: response.data.data.etag
    };
  } catch (error) {
    console.error('이미지 업로드 실패:', error);
    throw new Error('이미지 업로드에 실패했습니다.');
  }
};

/**
 * 마크다운 또는 HTML 텍스트에서 Base64 이미지를 찾아 업로드하고 URL로 변경
 */
export const processBase64Images = async (content: string): Promise<string> => {
  let processedContent = content;
  
  // 1. HTML img 태그 처리 (<img src="data:image/...">)
  const htmlImageRegex = /<img[^>]*src="(data:image\/[^;]+;base64,[^"]+)"[^>]*>/g;
  const htmlMatches = Array.from(content.matchAll(htmlImageRegex));
  
  for (const match of htmlMatches) {
    try {
      const fullMatch = match[0];
      const base64Data = match[1];
      
      // 이미지 업로드
      const uploadResult = await uploadBase64Image(base64Data);
      
      // img 태그의 src를 URL로 변경
      const newImageTag = fullMatch.replace(base64Data, uploadResult.url);
      processedContent = processedContent.replace(fullMatch, newImageTag);
      
      console.log(`HTML 이미지 업로드 완료: ${uploadResult.url}`);
    } catch (error) {
      console.error('HTML Base64 이미지 처리 실패:', error);
      // 실패한 이미지는 원본 유지
    }
  }
  
  // 2. 마크다운 이미지 처리 (![alt](data:image/...) - 하위 호환성 유지
  const markdownImageRegex = /!\[([^\]]*)\]\(data:image\/[^;]+;base64,[^)]+\)/g;
  const markdownMatches = Array.from(processedContent.matchAll(markdownImageRegex));
  
  for (const match of markdownMatches) {
    try {
      const fullMatch = match[0];
      const altText = match[1];
      
      // Base64 데이터 추출
      const base64Match = fullMatch.match(/data:image\/[^;]+;base64,[^)]+/);
      if (!base64Match) continue;
      
      const base64Data = base64Match[0];
      
      // 이미지 업로드
      const uploadResult = await uploadBase64Image(base64Data);
      
      // 마크다운 이미지 태그를 URL로 변경
      const newImageTag = `![${altText}](${uploadResult.url})`;
      processedContent = processedContent.replace(fullMatch, newImageTag);
      
      console.log(`마크다운 이미지 업로드 완료: ${uploadResult.url}`);
    } catch (error) {
      console.error('마크다운 Base64 이미지 처리 실패:', error);
      // 실패한 이미지는 원본 유지
    }
  }
  
  return processedContent;
};

/**
 * 이미지 크기 조정 (최대 1920px 너비)
 */
export const resizeImage = (file: File, maxWidth: number = 1920): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // 원본 크기 계산
      const { width, height } = img;
      
      // 크기 조정이 필요한지 확인
      if (width <= maxWidth) {
        // 크기 조정이 불필요한 경우 원본 반환
        canvas.width = width;
        canvas.height = height;
      } else {
        // 비율을 유지하면서 크기 조정
        const ratio = maxWidth / width;
        canvas.width = maxWidth;
        canvas.height = height * ratio;
      }

      // 이미지 그리기
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Blob으로 변환
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('이미지 크기 조정 실패'));
          }
        },
        'image/jpeg',
        0.9 // 품질 90%
      );
    };

    img.onerror = () => reject(new Error('이미지 로드 실패'));
    
    // 파일을 Data URL로 변환하여 이미지 로드
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

const uploadService = {
  uploadBase64Image,
  processBase64Images,
  resizeImage,
};

export default uploadService;